import { Routes } from '@angular/router';
import { authGuard } from './guards/auth/auth.guard';
import { organizationGuard } from './guards/organization/organization-guard';
import { reverseAuthGuard } from './guards/reverse-auth/reverse-auth-guard';
import {
  unverifiedGuard,
  verifiedGuard,
} from './guards/email-verification/email-verification.guard';
import { preFetchResolverFactory } from './resolvers/pre-fetch/pre-fetch.resolver';
import { moduleGuardFactory } from './guards/module/module.guard';
import { subscriptionGuard } from './guards/subscription/subscription.guard';
import { portalGuard } from './guards/portal/portal.guard';
import { billingAccessGuard } from './guards/billing-access/billing-access.guard';
import { OrganizationModule } from './enums/organization-module.enum';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadChildren: () => import('./pages/dashboard/dashboard.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    canMatch: [subscriptionGuard],
    canActivateChild: [organizationGuard],
    resolve: preFetchResolverFactory('user', 'organization', 'saleStats', 'inventoryStats'),
  },
  {
    path: 'auth',
    loadChildren: () => import('./pages/auth/auth.routes').then((m) => m.routes),
    data: {
      hideTopbar: true,
      hideSidebar: true,
    },
    canActivate: [reverseAuthGuard],
    canActivateChild: [reverseAuthGuard],
  },
  {
    path: 'organizations',
    loadChildren: () => import('./pages/organizations/organizations.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    resolve: preFetchResolverFactory('user'),
  },
  {
    path: 'products',
    loadChildren: () => import('./pages/products/products.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    canMatch: [subscriptionGuard, moduleGuardFactory(OrganizationModule.Products)],
    canActivateChild: [organizationGuard],
    resolve: preFetchResolverFactory('user', 'organization'),
  },
  {
    path: 'orders',
    loadChildren: () => import('./pages/orders/orders.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    canMatch: [subscriptionGuard, moduleGuardFactory(OrganizationModule.Orders)],
    canActivateChild: [organizationGuard],
    resolve: preFetchResolverFactory('user', 'organization'),
  },
  {
    // Incoming purchase orders (Zamówienia) from portal buyers - producer side.
    path: 'purchase-orders',
    loadChildren: () =>
      import('./pages/purchase-orders/purchase-orders.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    canMatch: [subscriptionGuard, moduleGuardFactory(OrganizationModule.Orders)],
    canActivateChild: [organizationGuard],
    resolve: preFetchResolverFactory('user', 'organization'),
  },
  {
    path: 'inventory',
    loadChildren: () => import('./pages/inventory/inventory.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    canMatch: [subscriptionGuard, moduleGuardFactory(OrganizationModule.Inventory)],
    canActivateChild: [organizationGuard],
    resolve: preFetchResolverFactory('user', 'organization'),
  },
  {
    path: 'customers',
    loadChildren: () => import('./pages/customers/customer.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    canMatch: [subscriptionGuard, moduleGuardFactory(OrganizationModule.Customers)],
    canActivateChild: [organizationGuard],
    resolve: preFetchResolverFactory('user', 'organization'),
  },
  {
    path: 'calendar',
    loadComponent: () => import('./pages/calendar/calendar').then((m) => m.Calendar),
    canActivate: [authGuard, verifiedGuard],
    canMatch: [subscriptionGuard, moduleGuardFactory(OrganizationModule.Calendar)],
    canActivateChild: [organizationGuard],
    resolve: preFetchResolverFactory('user', 'organization'),
  },
  {
    path: 'settings',
    loadChildren: () => import('./pages/settings/settings.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    canActivateChild: [organizationGuard],
    resolve: preFetchResolverFactory('user', 'organization'),
  },
  {
    path: 'onboarding',
    children: [
      {
        path: 'plan',
        loadComponent: () =>
          import('./pages/onboarding/plan-selection/plan-selection').then((m) => m.PlanSelection),
        canActivate: [authGuard, verifiedGuard, billingAccessGuard],
        canActivateChild: [organizationGuard],
        data: { hideTopbar: true, hideSidebar: true },
        resolve: preFetchResolverFactory('user', 'organization'),
      },
      {
        path: 'locked',
        loadComponent: () =>
          import('./pages/onboarding/locked/subscription-locked').then((m) => m.SubscriptionLocked),
        canActivate: [authGuard, verifiedGuard, organizationGuard],
        data: { hideTopbar: true, hideSidebar: true },
        resolve: preFetchResolverFactory('user', 'organization'),
      },
      {
        path: 'success',
        loadComponent: () =>
          import('./pages/onboarding/success/onboarding-success').then((m) => m.OnboardingSuccess),
        canActivate: [authGuard, verifiedGuard],
        data: { hideTopbar: true, hideSidebar: true },
      },
    ],
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./pages/auth/verify-email/verify-email').then((m) => m.VerifyEmail),
    canActivate: [authGuard, unverifiedGuard],
    data: { hideTopbar: true, hideSidebar: true },
  },
  {
    path: 'invite/accept',
    loadComponent: () => import('./pages/invite-accept/invite-accept').then((m) => m.InviteAccept),
    data: { hideTopbar: true, hideSidebar: true },
  },
  {
    // Portal invitation accept page - deliberately outside the guarded /portal
    // subtree (the invitee has no relation yet) and without auth guards (it
    // handles the signed-out state itself, like the team invite page).
    path: 'portal/invite',
    loadComponent: () =>
      import('./pages/portal/portal-invite/portal-invite').then((m) => m.PortalInvite),
    data: { hideTopbar: true, hideSidebar: true },
  },
  {
    path: 'portal',
    loadChildren: () => import('./pages/portal/portal.routes').then((m) => m.routes),
    canActivate: [authGuard, verifiedGuard],
    canMatch: [portalGuard],
    data: { hideTopbar: true, hideSidebar: true },
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
